/**
 * Real CacheClient — talks to the cache Worker (H1) over HTTP.
 *
 * Serialization contract (must mirror src/worker/handler.ts parseRequest):
 * provider travels as `?source=` (PROVIDER_QUERY_PARAM), type as `?type=`,
 * params flattened into the query; arrays comma-joined, scalars stringified.
 * The server reconstructs types schema-driven, so a single-element array
 * round-trips correctly (regression: covered in http-client.test.ts).
 *
 * Total: always resolves to a CacheResponse envelope, never rejects. Network
 * and protocol failures map to a static-message error envelope so a dead or
 * missing endpoint degrades the tile, never the board (ADR-002 §4).
 */
import type { CacheClient, CacheRequest, CacheResponse } from 'briefpane/contracts';
import { PROVIDER_QUERY_PARAM } from 'briefpane/contracts';
import { deriveCacheKey } from 'briefpane/schema';

/** Injectable fetch so tests never touch the network. */
export type FetchLike = (url: string, init?: RequestInit) => Promise<Response>;

const VALID_STATES: ReadonlySet<unknown> = new Set(['ok', 'stale', 'error', 'rateLimited']);

/** Flatten a CacheRequest into the wire query string (see contract above). */
export function serializeRequest(req: CacheRequest): string {
  const q = new URLSearchParams();
  q.set(PROVIDER_QUERY_PARAM, req.provider);
  q.set('type', req.type);
  for (const [key, value] of Object.entries(req.params)) {
    q.set(key, Array.isArray(value) ? value.map(String).join(',') : String(value));
  }
  return q.toString();
}

/** Client-side error envelope for transport/protocol failures. Static messages only. */
function transportError<Data>(req: CacheRequest, message: string): CacheResponse<Data> {
  const nowMs = Date.now();
  return {
    state: 'error',
    error: { code: 'upstream_error', message },
    meta: {
      cacheKey: deriveCacheKey({ type: req.type, provider: req.provider, params: req.params }),
      cached: false,
      fetchedAt: nowMs,
      expiresAt: nowMs,
      ttlSec: 0,
    },
  };
}

/**
 * Create the real HTTP cache client against the Worker endpoint.
 * `fetchFn` is injectable for tests; defaults to the platform fetch.
 */
export function createHttpCache(endpointPath: string, fetchFn?: FetchLike): CacheClient {
  const doFetch: FetchLike = fetchFn ?? ((url, init) => fetch(url, init));

  return {
    async fetch<Data>(req: CacheRequest): Promise<CacheResponse<Data>> {
      let res: Response;
      try {
        res = await doFetch(`${endpointPath}?${serializeRequest(req)}`, { method: 'GET' });
      } catch {
        return transportError<Data>(req, 'Could not reach the data service.');
      }

      let body: unknown;
      try {
        body = await res.json();
      } catch {
        return transportError<Data>(req, 'The data service returned an unreadable response.');
      }

      const state = (body as { state?: unknown } | null)?.state;
      if (!res.ok || body === null || typeof body !== 'object' || !VALID_STATES.has(state)) {
        return transportError<Data>(req, 'The data service returned an unexpected response.');
      }
      return body as CacheResponse<Data>;
    },
  };
}
